import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import Api from "../services/Api";
import {
  filterPaymentFailures,
  getNewFailures,
  markPaymentFailuresAsSeen,
  sortByCompletedOnDesc,
} from "../utils/paymentFailureNotifications";

const POLL_INTERVAL_MS = 60000;

export function usePaymentFailureNotifications({ enabled = true } = {}) {
  const [allFailures, setAllFailures] = useState([]);
  const [newFailures, setNewFailures] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef(null);
  const location = useLocation();

  const fetchFailures = useCallback(async () => {
    if (!enabled || !localStorage.getItem("fusionId")) return;
    try {
      setIsLoading(true);
      const response = await Api.get_completed_orders(1, 50);
      const orders = response?.data?.response?.trackOrderListing || [];
      const failures = sortByCompletedOnDesc(filterPaymentFailures(orders));
      const newOnes = sortByCompletedOnDesc(getNewFailures(failures));
      setAllFailures(failures);
      setNewFailures(newOnes);
      setBadgeCount(newOnes.length);
    } catch (error) {
      console.error("Error fetching payment failures:", error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    fetchFailures();
    intervalRef.current = setInterval(fetchFailures, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, fetchFailures]);

  useEffect(() => {
    if (enabled && location.pathname.startsWith("/order")) {
      fetchFailures();
    }
  }, [location.pathname, enabled, fetchFailures]);

  const markAsSeen = useCallback(() => {
    markPaymentFailuresAsSeen();
    setNewFailures([]);
    setBadgeCount(0);
  }, []);

  return {
    allFailures,
    newFailures,
    badgeCount,
    isLoading,
    fetchFailures,
    markAsSeen,
  };
}
